import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import MinimalHeader from "@/components/MinimalHeader";
import MinimalFooter from "@/components/MinimalFooter";
import ProjectWizard from "@/components/ProjectWizard";
import { blogPosts } from "@/data/blog";
import { homeContent } from "@/data/content";

const scrollToContact = () => {
  document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
};

const Hero = () => {
  const c = homeContent.hero;
  return (
    <section className="pt-32 pb-20 px-6">
      <div className="max-w-3xl mx-auto text-center">
        <p className="text-sm font-semibold uppercase tracking-widest text-primary mb-6 text-center indent-[0.2em]">
          {c.eyebrow}
        </p>
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.1] mb-6">
          {c.titlePrefix}
          <span className="text-primary">{c.titleHighlight}</span>
          {c.titleSuffix}
        </h1>
        <p className="text-lg text-muted-foreground leading-relaxed mb-6 max-w-2xl mx-auto">
          {c.subtitle}
        </p>
        <div className="inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm font-semibold mb-10 px-4 py-2 rounded-full bg-primary/10 text-primary">
          {c.badges.map((badge, i) => (
            <span key={badge} className="flex items-center gap-x-3">
              {i > 0 && <span className="opacity-50">·</span>}
              <span>{badge}</span>
            </span>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            onClick={scrollToContact}
            size="lg"
            className="rounded-full bg-primary hover:bg-primary/90 h-12 px-8 text-base font-semibold"
          >
            {c.primaryCta}
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
          <a
            href={`mailto:${c.contactEmail}`}
            className="inline-flex items-center justify-center rounded-full h-12 px-8 text-base font-semibold border border-border hover:bg-muted/50 transition-colors"
          >
            <Mail className="mr-2 w-4 h-4" />
            {c.contactEmail}
          </a>
        </div>
      </div>
    </section>
  );
};

const SectionCTA = ({ label }) => (
  <div className="mt-12 flex justify-center">
    <Button
      onClick={scrollToContact}
      size="lg"
      className="rounded-full bg-primary hover:bg-primary/90 h-12 px-8 text-base font-semibold shadow-lg shadow-primary/15 group"
    >
      {label}
      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
    </Button>
  </div>
);

const WhyCustom = () => {
  const c = homeContent.why;
  return (
    <section className="py-20 px-6 border-t border-border/50">
      <div className="max-w-5xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs font-bold uppercase tracking-widest text-primary mb-4">
            {c.eyebrow}
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-5">
            {c.title}
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed">
            {c.subtitle}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {c.points.map((p) => (
            <div
              key={p.title}
              className="p-6 rounded-2xl border border-border bg-card"
            >
              <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center mb-4">
                <Check className="w-5 h-5 text-primary" strokeWidth={2.5} />
              </div>
              <h3 className="font-semibold text-lg mb-2">{p.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
        <SectionCTA label={c.ctaLabel} />
      </div>
    </section>
  );
};

const Process = () => {
  const c = homeContent.process;
  return (
    <section className="py-20 px-6 border-t border-border/50">
      <div className="max-w-5xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs font-bold uppercase tracking-widest text-primary mb-4">
            {c.eyebrow}
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-5">
            {c.title}
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed">
            {c.subtitle}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {c.steps.map((s) => (
            <div key={s.n}>
              <div className="text-4xl font-bold text-primary mb-3">{s.n}</div>
              <h3 className="font-semibold text-lg mb-2">{s.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
        <SectionCTA label={c.ctaLabel} />
      </div>
    </section>
  );
};

const Pricing = () => {
  const c = homeContent.pricing;
  return (
    <section id="pricing" className="py-20 px-6 border-t border-border/50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs font-bold uppercase tracking-widest text-primary mb-4">
            {c.eyebrow}
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-5">
            {c.title}
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed">
            {c.subtitle}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
          {c.tiers.map((tier) => (
            <div
              key={tier.name}
              className={`relative flex flex-col p-6 rounded-2xl bg-card transition-all ${
                tier.featured
                  ? "border-2 border-primary shadow-xl shadow-primary/10 md:-translate-y-2"
                  : "border border-border"
              }`}
            >
              {tier.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest whitespace-nowrap">
                  {tier.badge}
                </div>
              )}
              <div className="mb-6">
                <h3 className="text-xl font-bold mb-1">{tier.name}</h3>
                <p className="text-sm text-muted-foreground">{tier.tagline}</p>
              </div>
              <div className="mb-6">
                <p className="text-4xl font-bold text-primary leading-none">
                  {tier.price}
                  <span className="text-base font-medium text-muted-foreground">{tier.priceSuffix}</span>
                </p>
                <p className="text-xs text-muted-foreground mt-2">{tier.priceNote}</p>
              </div>
              <ul className="space-y-2.5 mb-6 flex-1">
                {tier.features.map((feat, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                    <span className="leading-snug">{feat}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={scrollToContact}
                className={`w-full rounded-full h-11 text-sm font-semibold ${
                  tier.featured
                    ? "bg-primary hover:bg-primary/90 text-primary-foreground"
                    : "bg-transparent border border-border hover:bg-muted text-foreground"
                }`}
              >
                {tier.cta}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
        {c.footnote && (
          <p className="text-center text-sm text-muted-foreground mt-10 max-w-2xl mx-auto">
            {c.footnote}
          </p>
        )}
      </div>
    </section>
  );
};

const Blog = () => {
  const c = homeContent.blog;
  const posts = blogPosts.slice(0, 3);
  return (
    <section className="py-20 px-6 border-t border-border/50">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              {c.title}
            </h2>
            <p className="text-muted-foreground text-lg">
              {c.subtitle}
            </p>
          </div>
          <Link
            to="/blog"
            className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:opacity-80 transition-opacity whitespace-nowrap"
          >
            {c.viewAllLabel}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="group bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/30 transition-colors"
            >
              <div className="aspect-[16/9] overflow-hidden bg-muted">
                <img
                  src={post.image}
                  alt={post.title.fr}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                  <span className="font-semibold text-primary">{post.category}</span>
                  <span>·</span>
                  <span>{post.readTime}</span>
                </div>
                <h3 className="font-semibold text-lg leading-snug mb-2 group-hover:text-primary transition-colors">
                  {post.title.fr}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
                  {post.excerpt.fr}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const WizardSection = () => {
  const c = homeContent.projectWizard;
  return (
    <section id="contact" className="py-20 px-6 border-t border-border/50">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            {c.title}
          </h2>
          <p className="text-muted-foreground text-lg">
            {c.subtitle}
          </p>
        </div>
        <ProjectWizard source="home-wizard" />
      </div>
    </section>
  );
};

const MinimalHomePage = () => {
  useEffect(() => {
    if (window.location.hash === "#contact") {
      const timer = setTimeout(() => {
        document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <div data-testid="homepage" className="min-h-screen bg-background text-foreground">
      <SEO
        title={homeContent.seo.title}
        description={homeContent.seo.description}
        path="/"
      />
      <MinimalHeader />
      <main>
        <Hero />
        <WhyCustom />
        <Process />
        <Pricing />
        <WizardSection />
        <Blog />
      </main>
      <MinimalFooter />
    </div>
  );
};

export default MinimalHomePage;
